import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AppContext';
import { ShieldCheck, UserCheck, Layers, ArrowRight, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [extEmployees, setExtEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      fetch('https://user.jobes24x7.com/api/employee_education/combined-list').then(r => r.json())
    ])
      .then(([s, e]) => {
        setStats(s.data);
        if (e?.data?.data) setExtEmployees(e.data.data);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="flex-center" style={{ height: 300 }}><span className="spinner" style={{ width: 36, height: 36 }} /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome, {user?.name?.split(' ')[0]} 👋</h1>
          <p className="page-subtitle">
            You are a <strong style={{ color: 'var(--brand)' }}>{user?.role_type === 'panel_admin' ? 'Panel Admin' : 'Manage Admin'}</strong>
            {' '}managing {stats?.panels?.length || 0} panel(s)
          </p>
        </div>
        <Link to="/admin/roles" className="btn btn-primary"><ShieldCheck size={15} /> Create Role</Link>
      </div>

      {/* Stats */}
      {user?.userType === 'admin' ? (
        <div className="grid-3 mb-6">
          {[
            { label: 'My Panels', value: stats?.panels?.length || 0, icon: Building2, color: '#6366f1', link: '/admin/roles' },
            { label: 'Roles Created', value: stats?.roles || 0, icon: ShieldCheck, color: '#10b981', link: '/admin/roles' },
            { label: 'Employees', value: stats?.subAdmins || 0, icon: UserCheck, color: '#f59e0b', link: '/admin/employees' },
          ].map(({ label, value, icon: Icon, color, link }) => (
            <Link key={label} to={link} style={{ textDecoration: 'none' }}>
              <div className="stat-card">
                <div className="stat-icon-wrap" style={{ background: color + '18' }}>
                  <Icon size={22} color={color} />
                </div>
                <div>
                  <div className="stat-value">{value}</div>
                  <div className="stat-label">{label}</div>
                  <div className="stat-change neutral" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                    <ArrowRight size={12} /> View
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid-1 mb-6">
          <div className="stat-card" style={{ maxWidth: 400 }}>
            <div className="stat-icon-wrap" style={{ background: '#6366f118' }}>
              <Building2 size={22} color="#6366f1" />
            </div>
            <div>
              <div className="stat-value">{stats?.panels?.length || 0}</div>
              <div className="stat-label">Assigned Panels</div>
              <p className="text-xs text-muted mt-2">You have access to these panels via assigned roles.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid-2">
        {/* My Panels */}
        <div className="card card-pad">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>My Assigned Panels</h2>
          {!stats?.panels?.length ? (
            <p className="text-muted text-sm">No panels assigned to you yet. Contact Admin.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {stats.panels.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--bg-base)', border: '1px solid var(--border)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '.875rem' }}>{p.name}</div>
                    <div className="text-muted text-sm">{p.code}</div>
                  </div>
                  {p.assigned_role && <span className="badge badge-panel">{p.assigned_role}</span>}
                  <span className="badge badge-active">Active</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Employees - Only for Admin */}
        {user?.userType === 'admin' && (
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700 }}>Recent Employees</h2>
              <Link to="/admin/employees" style={{ fontSize: '.78rem', color: 'var(--brand)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                View all <ArrowRight size={13} />
              </Link>
            </div>
            {!stats?.recentSubAdmins?.length ? (
              <p className="text-muted text-sm">No employees created yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {stats.recentSubAdmins.map((s, i) => {
                  const ext = extEmployees.find(e => String(e.user_main_id) === String(s.user_main_id));
                  const name = ext?.user_name || ext?.name || 'User ' + s.user_main_id;
                  const email = ext?.email || 'No Email';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div className="user-avatar" style={{ width: 34, height: 34, fontSize: '.75rem' }}>
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                        <div className="text-muted text-sm">{email}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
